import { useCallback, useEffect, useState } from "react";
import { getLocations } from "src/services/product.service";

// Shared list of product locations. Every component using this hook sees the
// same list, and refresh() re-fetches for all of them at once, so a location
// created by a bulk move shows up in the filter chips and pickers without a
// page reload. Locations are free text on Product, so the list is whatever
// is currently in use.
let cache: string[] | null = null;
let inflight: Promise<string[]> | null = null;
const listeners = new Set<(locations: string[]) => void>();

const load = (): Promise<string[]> => {
  if (!inflight) {
    inflight = new Promise<string[]>((resolve, reject) => {
      getLocations(
        (locations: string[]) => {
          cache = locations;
          listeners.forEach((listener) => listener(locations));
          resolve(locations);
        },
        (err) => reject(err)
      );
    }).finally(() => {
      inflight = null;
    });
  }
  return inflight;
};

export const useLocations = () => {
  const [locations, setLocations] = useState<string[]>(cache ?? []);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    listeners.add(setLocations);
    if (cache) setLocations(cache);
    load().catch((e) => {
      console.error("Failed to load locations:", e);
      setError(e);
    });
    return () => {
      listeners.delete(setLocations);
    };
  }, []);

  const refresh = useCallback(
    () =>
      load().catch((e) => {
        console.error("Failed to refresh locations:", e);
        setError(e);
        return [] as string[];
      }),
    []
  );

  return { locations, error, refresh };
};
